<template>
  <el-dialog
    :visible="visible" :title="`Edit collection · ${row ? row.name : ''}`"
    width="min(760px, 94vw)" top="5vh" :close-on-click-modal="false" @close="$emit('close')"
  >
    <div v-loading="loading" class="collection-editor">
      <el-alert v-if="error" :title="error" type="error" show-icon :closable="false" />
      <template v-if="form">
        <p class="editor-note">Edit collection details and settings here. Group and session content is managed separately.</p>
        <el-tabs v-model="tab">
          <el-tab-pane label="Details" name="details">
            <div class="editor-grid">
              <label class="wide">Title <el-input v-model="form.title" maxlength="100" show-word-limit /></label>
              <label class="wide">Subtitle <el-input v-model="form.sub_title" maxlength="200" show-word-limit /></label>
              <label class="wide">Description <el-input v-model="form.description" type="textarea" :rows="5" maxlength="10000" show-word-limit /></label>
              <label class="wide">Grand takeaway <el-input v-model="form.grand_take_away" type="textarea" :rows="3" maxlength="4000" show-word-limit /></label>
              <label>Certificate knowledge phrase <el-input v-model="form.collection_knowledge" maxlength="120" show-word-limit /></label>
              <label>Version identifier <el-input v-model="form.version_identifier" maxlength="100" show-word-limit /></label>
              <label class="wide">About the curator <el-input v-model="form.author_bio" type="textarea" :rows="3" maxlength="4000" show-word-limit /></label>
              <label class="wide">Curator credential <el-input v-model="form.author_credential" maxlength="500" show-word-limit /></label>
              <label class="wide">Announcement blurb <el-input v-model="form.announcement_blurb" type="textarea" :rows="3" maxlength="4000" show-word-limit /></label>
              <label class="wide">Cover image URL <el-input v-model="form.cover_url" maxlength="2000" :disabled="uploadingCover" placeholder="https://…" /></label>
              <div class="wide cover-upload-field">
                <input ref="coverFileInput" class="cover-file-input" type="file" accept="image/*" aria-label="Choose a cover image" @change="handleCoverSelection" />
                <el-button size="small" icon="el-icon-upload2" :loading="uploadingCover" :disabled="uploadingCover" @click="$refs.coverFileInput.click()">{{ uploadingCover ? 'Uploading cover…' : 'Upload cover image' }}</el-button>
                <small>Images are cropped to 5:7 (600 × 840) and compressed. Save changes to apply the new cover.</small>
              </div>
              <img v-if="form.cover_url" :src="form.cover_url" alt="Collection cover preview" class="cover-preview" />
              <label class="wide">Cover prompt <el-input v-model="form.cover_prompt" type="textarea" :rows="3" maxlength="4000" show-word-limit /></label>
              <label class="wide">Promotional video YouTube URL
                <el-input v-model="form.youtube_url" maxlength="2000" clearable placeholder="https://www.youtube.com/watch?v=..." />
                <small>Shown as an embedded video on the public collection page.</small>
              </label>
            </div>
          </el-tab-pane>
          <el-tab-pane label="Discovery" name="discovery">
            <div class="editor-grid">
              <label class="wide">Genre tags <el-select v-model="form.genre_tags" multiple filterable allow-create default-first-option :multiple-limit="2" placeholder="Type a tag and press Enter" /></label>
              <label class="wide">Tags <el-select v-model="form.tags" multiple filterable allow-create default-first-option :multiple-limit="20" placeholder="Type a tag and press Enter" /></label>
              <label class="wide">Key questions <el-select v-model="form.key_questions" multiple filterable allow-create default-first-option :multiple-limit="5" placeholder="Type a question and press Enter" /></label>
              <label class="wide">Key concepts <el-select v-model="form.key_concepts" multiple filterable allow-create default-first-option :multiple-limit="10" placeholder="Type a concept and press Enter" /></label>
              <label class="wide">Catalogue sections
                <el-select v-model="form.catalogue_section_codes" multiple filterable placeholder="Choose sections">
                  <el-option v-for="option in catalogueSections" :key="option.value" :label="option.label" :value="option.value" />
                </el-select>
              </label>
              <label class="wide">Industries
                <el-select v-model="form.industry_codes" multiple filterable placeholder="Choose industries">
                  <el-option v-for="option in industries" :key="option.value" :label="option.label" :value="option.value" />
                </el-select>
              </label>
              <label class="wide">Certificate tracks
                <el-select v-model="form.track_ids" multiple filterable :multiple-limit="3" :loading="tracksLoading" placeholder="Choose up to three tracks">
                  <el-option v-for="track in tracks" :key="track.id" :label="track.name" :value="track.id" />
                </el-select>
              </label>
              <div class="wide choices-field"><strong>Target learner levels</strong>
                <el-checkbox-group v-model="form.target_audience_stages">
                  <el-checkbox v-for="option in learnerLevels" :key="option.value" :label="option.value">{{ option.label }}</el-checkbox>
                </el-checkbox-group>
                <small>Leave all unchecked for all learner levels.</small>
              </div>
            </div>
          </el-tab-pane>
          <el-tab-pane label="Access & pricing" name="access">
            <div class="editor-grid">
              <label>Publication status
                <el-select v-model="form.status">
                  <el-option v-for="status in statuses" :key="status" :label="status" :value="status" />
                </el-select>
              </label>
              <label>Estimated launch date <el-date-picker v-model="form.teaser_release_date" type="date" value-format="yyyy-MM-dd" placeholder="Optional" clearable /></label>
              <label>Narrative mode
                <el-select v-model="form.narrative_mode">
                  <el-option label="Answer first" value="answer_first" />
                  <el-option label="Answer last" value="answer_last" />
                </el-select>
              </label>
              <label>Country availability
                <el-select v-model="form.country_scope.mode" @change="countryModeChanged">
                  <el-option label="All countries" value="ALL" />
                  <el-option label="Only selected countries" value="WHITELIST" />
                  <el-option label="Except selected countries" value="BLACKLIST" />
                </el-select>
              </label>
              <label v-if="form.country_scope.mode !== 'ALL'" class="wide">Countries
                <el-select v-model="form.country_scope.country_codes" multiple filterable collapse-tags placeholder="Choose countries">
                  <el-option v-for="option in countries" :key="option.value" :label="option.label" :value="option.value" />
                </el-select>
              </label>
              <div class="switch-field"><span>Entire collection behind paywall</span><el-switch v-model="form.subscribers_only" /></div>
              <div class="switch-field"><span>Show in public search</span><el-switch v-model="form.shown_in_public_search" /></div>
              <div class="switch-field"><span>Visible in recommendations</span><el-switch v-model="form.visible_in_recommendation" /></div>
              <div class="switch-field"><span>Enable one-time purchase</span><el-switch v-model="form.single_purchase_enabled" /></div>
              <div class="switch-field"><span>Use custom price</span><el-switch v-model="form.has_custom_price" /></div>
              <label>Base credits / price <el-input-number v-model="form.base_credits" :min="0" :max="1000000" :precision="2" :step="1" /></label>
              <label>Custom USD price <el-input-number v-model="form.usd_price" :min="0" :max="1000000" :precision="2" :step="1" /></label>
            </div>
          </el-tab-pane>
        </el-tabs>
      </template>
    </div>
    <span slot="footer" class="dialog-footer">
      <el-button @click="$emit('close')">Cancel</el-button>
      <el-button v-if="(error && !form) || conflict" @click="load">{{ conflict ? 'Reload collection' : 'Retry' }}</el-button>
      <el-button type="primary" :loading="saving" :disabled="loading || uploadingCover || !form || !isDirty || conflict" @click="save">Save changes</el-button>
    </span>
  </el-dialog>
</template>

<script>
import { showCollectionLaunchNotification } from '@/utils/collectionLaunchNotification'
import { getAdminCollectionFields, updateAdminCollectionFields, listCertTracks } from '@/apis/lfAdmin'
import { postRequest } from '@/apis/front'
import * as qiniu from 'qiniu-js'
import { CATALOGUE_SECTION_OPTIONS } from '@/constants/catalogueSections'
import { INDUSTRY_OPTIONS } from '@/constants/industries'
import { getCountryOptions, normalizeCountryScope } from '@/utils/countryAvailability'
import { prepareCollectionCover } from '@/utils/collectionCoverImage'

const COVER_IMAGE_DOMAIN = 'https://ap.learningfirst.io/'

const textFields = [
  'title', 'sub_title', 'description', 'grand_take_away', 'collection_knowledge',
  'author_bio', 'author_credential', 'announcement_blurb', 'cover_url',
  'cover_prompt', 'version_identifier', 'teaser_release_date', 'youtube_url'
]
const listFields = ['genre_tags', 'tags', 'key_questions', 'key_concepts', 'catalogue_section_codes', 'industry_codes', 'target_audience_stages', 'track_ids']
const booleanFields = [
  'subscribers_only', 'single_purchase_enabled', 'has_custom_price',
  'shown_in_public_search', 'visible_in_recommendation'
]
const editableFields = [...textFields, ...listFields, ...booleanFields, 'status', 'narrative_mode', 'base_credits', 'usd_price', 'country_scope']

export default {
  name: 'AdminCollectionEditor',
  props: {
    visible: Boolean,
    row: { type: Object, default: null }
  },
  data() {
    return { form: null, original: null, version: 0, loading: false, saving: false, uploadingCover: false, error: '', conflict: false, tab: 'details', requestId: 0, tracks: [], tracksLoading: false,
      statuses: ['PRIVATE', 'PREORDER', 'TEASER', 'REQUEST', 'PUBLISHED'],
      catalogueSections: CATALOGUE_SECTION_OPTIONS, industries: INDUSTRY_OPTIONS, countries: getCountryOptions(),
      learnerLevels: [
        { value: 'primary', label: 'Primary' }, { value: 'secondary', label: 'Secondary' },
        { value: 'collegiate', label: 'Collegiate' }, { value: 'professional', label: 'Professional' }
      ] }
  },
  computed: {
    changes() {
      if (!this.form || !this.original) return {}
      return editableFields.reduce((changes, key) => {
        if (JSON.stringify(this.form[key]) !== JSON.stringify(this.original[key])) changes[key] = this.form[key]
        return changes
      }, {})
    },
    isDirty() { return Object.keys(this.changes).length > 0 }
  },
  watch: {
    visible(value) { if (value) this.load(); else this.requestId++ }
  },
  mounted() { if (this.visible) this.load() },
  methods: {
    countryModeChanged(mode) { if (mode === 'ALL') this.form.country_scope.country_codes = [] },
    result(response) {
      const result = response && response.data
      if (!result || Number(result.code) !== 0 || !result.data) {
        const error = new Error(result && result.msg ? result.msg : 'Could not load collection')
        error.code = result && Number(result.code)
        throw error
      }
      return result.data
    },
    setForm(data) {
      const settings = data.settings || {}
      const form = {}
      textFields.forEach(key => {
        const value = key === 'youtube_url' ? data.video && data.video.youtube_url : data[key]
        form[key] = typeof value === 'string' ? value : ''
      })
      listFields.forEach(key => {
        const values = Array.isArray(data[key]) ? data[key] : []
        form[key] = key === 'track_ids'
          ? values.map(value => typeof value === 'string' ? value : (value && (value.$oid || value.$id)) || '').filter(Boolean)
          : values.slice()
      })
      booleanFields.forEach(key => {
        const value = key === 'shown_in_public_search' || key === 'visible_in_recommendation' ? settings[key] : data[key]
        form[key] = value === undefined ? key === 'visible_in_recommendation' : value === true
      })
      form.status = data.status || 'PRIVATE'
      form.narrative_mode = data.narrative_mode || 'answer_last'
      form.base_credits = Number(data.base_credits) || 0
      form.usd_price = Number(data.usd_price) || 0
      form.country_scope = normalizeCountryScope(data)
      this.form = form
      this.original = JSON.parse(JSON.stringify(form))
      this.version = Number(data.content_version) || 0
    },
    async load() {
      if (!this.row) return
      const requestId = ++this.requestId
      this.loading = true
      this.uploadingCover = false
      this.error = ''
      this.conflict = false
      this.form = null
      try {
        const data = this.result(await getAdminCollectionFields(this.row.id))
        if (requestId === this.requestId) { this.setForm(data); this.loadTracks() }
      } catch (error) {
        if (requestId === this.requestId) this.error = error.message
      } finally {
        if (requestId === this.requestId) this.loading = false
      }
    },
    async loadTracks() {
      this.tracksLoading = true
      try {
        const data = this.result(await listCertTracks())
        this.tracks = data.map(track => ({ id: typeof track._id === 'string' ? track._id : track._id.$oid || track._id.$id, name: track.track_name || 'Untitled track' }))
      } catch (error) { this.$message.error('Could not load certificate tracks') }
      finally { this.tracksLoading = false }
    },
    async save() {
      if (!this.form || !this.isDirty || this.saving || this.uploadingCover) return
      if (this.form.title.trim().length < 3) { this.$message.error('Title must be at least 3 characters'); this.tab = 'details'; return }
      const youtubeUrl = this.form.youtube_url.trim()
      if (youtubeUrl && !this.isValidYoutubeUrl(youtubeUrl)) { this.$message.error('Enter a valid YouTube video URL'); this.tab = 'details'; return }
      this.form.youtube_url = youtubeUrl
      this.form.teaser_release_date = this.form.teaser_release_date || ''
      if (this.form.country_scope.mode !== 'ALL' && !this.form.country_scope.country_codes.length) { this.$message.error('Choose at least one country'); this.tab = 'access'; return }
      this.saving = true
      this.error = ''
      try {
        const response = await updateAdminCollectionFields({ id: this.row.id, content_version: this.version, changes: this.changes })
        const data = this.result(response)
        this.setForm(data)
        this.$message.success('Collection updated')
        showCollectionLaunchNotification(this.$message, response.data.launch_notification)
        this.$emit('saved')
        this.$emit('close')
      } catch (error) {
        this.error = error.message
        this.conflict = error.code === -409
        this.$message.error(error.message)
      } finally { this.saving = false }
    },
    async handleCoverSelection(event) {
      const file = event.target.files[0]
      event.target.value = ''
      if (!file) return
      if (this.uploadingCover || !this.form) return false
      if (!file.type.startsWith('image/')) { this.$message.error('Cover must be an image file!'); return false }
      if (file.size >= 5 * 1024 * 1024) { this.$message.error('Cover size can not exceed 5MB!'); return false }

      const requestId = this.requestId
      const form = this.form
      this.uploadingCover = true
      try {
        const prepared = await prepareCollectionCover(file)
        if (requestId !== this.requestId || form !== this.form || !this.visible) return false
        const tokenResponse = await postRequest('getLearningfirstQiniuToken', {})
        const tokenResult = tokenResponse && tokenResponse.data
        if (!tokenResult || Number(tokenResult.code) !== 0 || !tokenResult.data) {
          throw new Error(tokenResult && tokenResult.msg ? tokenResult.msg : 'Could not get an upload token')
        }
        if (requestId !== this.requestId || form !== this.form || !this.visible) return false
        const key = await new Promise((resolve, reject) => {
          qiniu.upload(prepared, Date.now() + prepared.name, tokenResult.data).subscribe({
            complete: response => resolve(response.key),
            error: reject
          })
        })
        if (requestId !== this.requestId || form !== this.form || !this.visible) return false
        if (!key) throw new Error('Upload did not return a cover URL')
        this.form.cover_url = COVER_IMAGE_DOMAIN + key
        this.$message.success('Cover uploaded. Save changes to apply it.')
      } catch (error) {
        if (requestId === this.requestId && this.visible) this.$message.error(error.message || 'Failed to upload cover')
      } finally {
        if (requestId === this.requestId) this.uploadingCover = false
      }
      return false
    },
    isValidYoutubeUrl(value) {
      let url
      try { url = new URL(value) } catch (error) { return false }
      if (!['https:', 'http:'].includes(url.protocol) || url.username || url.password || url.port) return false
      const host = url.hostname.toLowerCase()
      const parts = url.pathname.split('/').filter(Boolean)
      let id = ''
      if (['youtu.be', 'www.youtu.be'].includes(host) && parts.length === 1) id = parts[0]
      else if (['youtube.com', 'www.youtube.com', 'm.youtube.com', 'youtube-nocookie.com', 'www.youtube-nocookie.com'].includes(host)) {
        if (parts.length === 1 && parts[0] === 'watch') id = url.searchParams.get('v') || ''
        else if (parts.length === 2 && ['shorts', 'live', 'embed'].includes(parts[0])) id = parts[1]
      }
      return /^[a-zA-Z0-9_-]{11}$/.test(id)
    }
  }
}
</script>

<style scoped>
.collection-editor { min-height: 250px; max-height: 68vh; overflow-y: auto; }
.editor-note { margin: 0 0 14px; color: #718096; font-size: 12px; }
.editor-grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 18px 20px; padding: 14px 2px 20px; }
.editor-grid label { display: flex; flex-direction: column; gap: 7px; font-size: 12px; font-weight: 600; color: #40536a; }
.editor-grid .wide { grid-column: 1 / -1; }
.editor-grid .el-select, .editor-grid .el-date-editor, .editor-grid .el-input-number { width: 100%; }
.switch-field { display: flex; justify-content: space-between; align-items: center; gap: 10px; font-size: 12px; color: #40536a; }
.choices-field { display: flex; flex-direction: column; gap: 8px; font-size: 12px; color: #40536a; }
.choices-field small { color: #718096; }
.editor-grid label small { font-weight: 400; color: #718096; }
.cover-preview { max-width: 130px; max-height: 180px; object-fit: contain; }
.cover-upload-field { display: flex; align-items: center; gap: 12px; flex-wrap: wrap; }
.cover-file-input { display: none; }
.cover-upload-field small { color: #718096; font-size: 12px; }
@media (max-width: 600px) { .editor-grid { grid-template-columns: 1fr; } }
</style>
